// Renders the row of pending attachment chips in the composer for
// NON-IMAGE files (PDFs, docs, zips, etc.).
//
// Images are skipped here because a pasted screenshot is inserted directly
// into the rich editor body (see `RichBodyEditor`'s paste handler), where it's
// visible in place. This comment used to say images went through an
// `<InlineImagePreviews>` component at the top of the composer — that
// component existed but was never rendered anywhere, so the comment described
// an integration that didn't exist. It has been deleted.
//
// Pure presentational — `useComposerAttachments` owns the state.

import { X, Loader2, Paperclip, AlertTriangle } from 'lucide-react';
import { humanFileSize, type PendingAttachment } from '@/lib/use-composer-attachments';
import { cn } from '@/lib/utils';

interface Props {
  items: PendingAttachment[];
  onRemove: (localId: string) => void;
}

export function AttachmentsRow({ items, onRemove }: Props) {
  // Skip images — they're already visible inline in the editor body. This
  // component only handles the small chips for non-image attachments.
  const filtered = items.filter((it) => it.previewUrl === null);
  if (filtered.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {filtered.map((it) => {
        const isUploading = it.state === 'uploading';
        const hasError = it.state === 'error';
        return (
          <div
            key={it.localId}
            className={cn(
              'group relative inline-flex items-center gap-1.5 rounded-md border bg-background py-1 pl-2 pr-1 text-xs transition-colors',
              hasError ? 'border-tint-critical-ring text-tint-critical-fg' : 'border-border/70 text-foreground/85',
            )}
            title={hasError ? it.errorMessage : `${it.file.name} · ${humanFileSize(it.file.size)}`}
          >
            {hasError ? (
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            ) : (
              <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            )}
            <span className="max-w-[12rem] truncate">{it.file.name}</span>
            <span className="text-3xs text-muted-foreground">
              {humanFileSize(it.file.size)}
            </span>
            {isUploading ? (
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" aria-label="Uploading" />
            ) : null}
            <button
              type="button"
              onClick={() => onRemove(it.localId)}
              className="ml-0.5 rounded-full p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
              aria-label={`Remove ${it.file.name}`}
              disabled={isUploading}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
